import React, { useEffect, useState } from "react";
import CustomButton from "../../components/form/CustomButton";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import { getNewPlan, paymentsMethodsResource, purchaseLoadPackage } from "../../helpers/configRoutes/backend_helper";
import { showFormErrors } from "../../helpers/showFormErrors";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInput from "../../components/form/CustomInput";
import CustomSelect from "../../components/form/CustomSelect";
import { formatPlanLimit, getAiFeatures, getDowngradeAdjustmentMessage, getPlanDowngradeImpact } from "./planFeatureHelpers";

export default function ConfirmPayment({ modal, setModal }) {

    const [loading, setLoading] = useState(false);
    const [addMethod, setAddMethod] = useState(false)
    const [selectedMethod, setSelectedMethod] = useState({});
    const [paymentMethods, setPaymentMethods] = useState(false)
    const [changeMethod, setChangeMethod] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")

    useEffect(() => {
        const getMethods = async () => {
            const response = await paymentsMethodsResource("get", { params: { selext: true } });
            if (response.status) {
                let method = response.methods.length
                    ? response.methods.find((m) => m.principal)
                    : {}
                setPaymentMethods(response.methods)
                setSelectedMethod(method)
            }
        };
        getMethods();
    }, [addMethod]);

    const plan = modal?.plan;
    const loadPackage = modal?.package;
    const checkoutType = modal?.checkoutType || "plan";
    const checkoutItem = checkoutType === "load-package" ? loadPackage : plan;
    const isPlanCheckout = checkoutType === "plan";

    const price = Number(checkoutItem?.price || 0);
    const taxes = Number(checkoutItem?.taxes || 0);
    const taxAmount = (price * taxes) / 100;
    const total = price + taxAmount;
    const aiFeatures = isPlanCheckout ? getAiFeatures(plan) : { included: [], all: [] };
    const isTrialUpgrade = Boolean(
        isPlanCheckout &&
        modal?.company?.subscription?.is_trial &&
        modal?.company?.lastSubs?.plan_id === plan?.id
    );
    const downgradeImpact = isPlanCheckout
        ? (modal?.downgradeImpact || getPlanDowngradeImpact(plan, modal?.usageFeatures || {}))
        : { hasImpact: false, impacted: [] };
    const requiresAdjustment = isPlanCheckout && downgradeImpact.hasImpact;

    const save = async () => {
        if (requiresAdjustment) {
            setErrorMessage(getDowngradeAdjustmentMessage(downgradeImpact.impacted));
            return;
        }

        if (!checkoutItem?.id || !selectedMethod?.id) return;

        setLoading(true);
        setErrorMessage("");
        try {
            const response = isPlanCheckout
                ? await getNewPlan({
                    plan_id: plan.id,
                    method_id: selectedMethod.id,
                    recurrent_payment: false
                })
                : await purchaseLoadPackage({
                    package_id: loadPackage.id,
                    method_id: selectedMethod.id,
                });

            if (response.status) {
                window.dispatchEvent(new Event("company-subscription-updated"));
                setModal({ show: false, plan: null, package: null, checkoutType: "plan" });
                return;
            }

            setErrorMessage(
                response.message ||
                (isPlanCheckout
                    ? "Unable to update the subscription right now."
                    : "Unable to purchase the load package right now.")
            );
        } catch (e) {
            console.error(e);
            setErrorMessage(
                isPlanCheckout
                    ? "Unable to update the subscription right now."
                    : "Unable to purchase the load package right now."
            );
        } finally {
            setLoading(false);
        }
    };

    const stripe = useStripe();
    const elements = useElements();

    const baseItem = {
        id: null,
        card_name: "",
        alias: "",
        principal: false,
    };

    const validation = useFormik({
        initialValues: baseItem,
        enableReinitialize: true,
        validationSchema: Yup.object({
            card_name: Yup.string().required(
                "Card holder name is required."
            ),
            alias: Yup.string().nullable(),
            principal: Yup.boolean(),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);

            try {
                if (!stripe || !elements) {
                    setErrors({
                        card_name: "Stripe is not available at the moment.",
                    });
                    setLoading(false);
                    return;
                }

                const cardElement = elements.getElement(CardElement);
                if (!cardElement) {
                    setErrors({
                        card_name: "Unable to read card information.",
                    });
                    setLoading(false);
                    return;
                }

                const { error, paymentMethod } =
                    await stripe.createPaymentMethod({
                        type: "card",
                        card: cardElement,
                        billing_details: { name: values.card_name },
                    });

                if (error) {
                    throw new Error(error.message);
                }

                const dataToSend = {
                    ...values,
                    paymentMethodId: paymentMethod.id,
                    last4: paymentMethod.card.last4,
                    exp_month: paymentMethod.card.exp_month,
                    exp_year: paymentMethod.card.exp_year
                };

                const response = await paymentsMethodsResource(
                    values.id ? "put" : "post",
                    dataToSend,
                    values.id
                );

                if (!response.status) {
                    throw new Error(
                        response.errors || "Error saving payment method."
                    );
                }

                setSelectedMethod(response.method);
                setAddMethod(false)
            } catch (err) {
                setErrors({ card_name: err.message });
            } finally {
                setLoading(false);
            }
        },
    });

    const onChangeMethod = (method_id) => {
        const paymentMet = paymentMethods.find((m) => String(m.id) === String(method_id))
        setSelectedMethod(paymentMet)

        setChangeMethod(false)
    }

    return (
        <BaseModalAnimated
            modal={modal}
            setModal={setModal}
            title="Checkout"
            size="max-w-4xl"
        >
            <div className="w-full mx-auto shadow-xl">

                <div className="mb-2 text-center">
                    <h2 className="text-xl font-bold text-cs-dark">
                        Checkout
                    </h2>
                    <p className="text-sm text-cs-muted">
                        {isPlanCheckout
                            ? (isTrialUpgrade
                                ? "Complete your trial with a full subscription"
                                : "Review your subscription and payment method")
                            : "Review your load package and payment method"}
                    </p>
                </div>

                <div className="border rounded-xl p-1 bg-cs-bg mb-2">
                    <div className="flex justify-between items-center mb-3">
                        <div>
                            <h3 className="font-semibold text-cs-dark">
                                {isPlanCheckout ? `${plan.name} Plan` : loadPackage?.name}
                            </h3>
                            <p className="text-xs text-cs-muted">
                                {isPlanCheckout ? "Monthly billing" : "One-time load package purchase"}
                            </p>
                        </div>
                        <span className="text-lg font-bold text-cs-primary">
                            ${price.toFixed(2)}
                        </span>
                    </div>

                    {isPlanCheckout ? (
                        <>
                            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-cs-dark">
                                <li className="flex items-center gap-1 justify-center">
                                    🚚 <b>{formatPlanLimit(plan.trucks)}</b> trucks
                                </li>

                                <li className="flex items-center gap-1 justify-center">
                                    👥 <b>{formatPlanLimit(plan.users)}</b> users
                                </li>

                                {plan.day_trial > 0 && (
                                    <li className="flex items-center gap-1 justify-center">
                                        ⏱ <b>{plan.day_trial}</b> days free trial
                                    </li>
                                )}
                            </ul>

                            {downgradeImpact.hasImpact && (
                                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-red-700">
                                        Adjustment required before downgrade
                                    </p>
                                    <p className="mt-1 text-xs text-cs-muted">
                                        {getDowngradeAdjustmentMessage(downgradeImpact.impacted)}
                                    </p>
                                    <p className="mt-1 text-xs text-cs-muted">
                                        Remove the extra records first, then come back to confirm the plan change.
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {downgradeImpact.impacted.map((item) => (
                                            <span
                                                key={item.key}
                                                className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-red-700 border border-red-200"
                                            >
                                                {item.label}: {item.used}/{item.limit}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-3 rounded-xl border border-cs-border/70 bg-white px-3 py-3">
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cs-primary-dark">
                                            AI subscription included
                                        </p>
                                        <p className="text-[11px] text-cs-muted mt-0.5">
                                            These AI tools will be active with this subscription.
                                        </p>
                                    </div>
                                    <span className="rounded-full bg-cs-primary-soft px-2.5 py-1 text-[11px] font-medium text-cs-primary-dark">
                                        {aiFeatures.included.length} included
                                    </span>
                                </div>

                                <div className="mt-3 space-y-2">
                                    {aiFeatures.all.map((feature) => (
                                        <div
                                            key={feature.key}
                                            className={`rounded-lg border px-3 py-2 ${feature.enabled
                                                ? "border-cs-primary/20 bg-cs-primary-soft/30"
                                                : "border-cs-border bg-cs-bg-alt/70"
                                                }`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <p className="text-sm font-medium text-cs-dark">
                                                        {feature.label}
                                                    </p>
                                                    <p className="text-[11px] text-cs-muted mt-0.5">
                                                        {feature.description}
                                                    </p>
                                                </div>
                                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${feature.enabled
                                                    ? "bg-cs-primary text-white"
                                                    : "bg-cs-border text-cs-muted"
                                                    }`}>
                                                    {feature.enabled ? "Included" : "Not included"}
                                                </span>
                                            </div>

                                            {feature.enabled && (
                                                <p className="mt-2 text-[11px] font-medium text-cs-primary-dark">
                                                    {feature.limit}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-cs-dark">
                            <div className="rounded-xl border border-cs-border bg-white px-3 py-3 text-center">
                                <p className="text-xs uppercase tracking-[0.12em] text-cs-muted">Loads added</p>
                                <p className="mt-1 text-lg font-semibold text-cs-primary">+{loadPackage?.loads || 0}</p>
                            </div>
                            <div className="rounded-xl border border-cs-border bg-white px-3 py-3 text-center">
                                <p className="text-xs uppercase tracking-[0.12em] text-cs-muted">Validity</p>
                                <p className="mt-1 text-lg font-semibold text-cs-primary">{loadPackage?.validity_days || 0} day{Number(loadPackage?.validity_days || 0) !== 1 ? "s" : ""}</p>
                            </div>
                            <div className="rounded-xl border border-cs-border bg-white px-3 py-3 text-center">
                                <p className="text-xs uppercase tracking-[0.12em] text-cs-muted">Applies to</p>
                                <p className="mt-1 text-sm font-semibold text-cs-primary-dark">Current active subscription</p>
                            </div>
                        </div>
                    )}
                </div>

                {errorMessage && (
                    <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-xs text-red-700">
                        {errorMessage}
                    </div>
                )}

                {/* Payment method */}
                <div className="mb-1">
                    <h4 className="text-sm font-semibold text-cs-dark mb-2">
                        Payment method
                    </h4>
                    {addMethod ?
                        <>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    validation.handleSubmit();
                                    showFormErrors(validation);
                                    return false;
                                }}
                            >
                                <div className="bg-cs-bg rounded-2xl overflow-hidden">
                                    {/* Header contextual */}
                                    <div className="px-5 py-3 border-b border-cs-border bg-cs-surface flex items-center justify-between gap-3">
                                        <div className="flex flex-col gap-0.5">
                                            <p className="text-[10px] uppercase tracking-[0.16em] text-cs-primary-dark">
                                                Add new payment method
                                            </p>
                                            <p className="text-[9px] text-cs-muted">
                                                Your card data is processed securely via Stripe.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setAddMethod(false)}
                                            className="
                                                px-3 py-1.5 rounded-xl
                                                bg-cs-bg
                                                text-[9px] text-cs-muted
                                                hover:bg-cs-primary-soft hover:text-cs-primary
                                                border border-cs-border
                                                transition-all
                                            "
                                        >
                                            Close
                                        </button>
                                    </div>

                                    {/* Body */}
                                    <div className="p-2 bg-cs-bg-alt grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Card holder */}
                                        <CustomInput
                                            label="Card holder name"
                                            type="text"
                                            name="card_name"
                                            labelClassName="text-[10px] font-medium text-cs-muted mb-1"
                                            error={validation.errors.card_name}
                                            value={validation.values.card_name}
                                            handleChange={validation.handleChange}
                                            placeholder="As shown on the card"
                                        />

                                        {/* Card info (Stripe) */}
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] font-medium text-cs-muted">
                                                Card information
                                            </label>
                                            <div
                                                className="
                                                        w-full py-2 px-2.5 rounded-xl
                                                        border border-cs-border
                                                        bg-cs-bg
                                                        text-[10px]
                                                        focus-within:border-cs-primary
                                                        transition-colors
                                                    "
                                            >
                                                <CardElement
                                                    options={{
                                                        style: {
                                                            base: {
                                                                fontSize: "12px",
                                                                color: "#151515",
                                                                "::placeholder": {
                                                                    color: "#9CA3AF",
                                                                },
                                                            },
                                                            invalid: {
                                                                color: "#EF4444",
                                                            },
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Alias */}
                                        <CustomInput
                                            label="Alias (optional)"
                                            type="text"
                                            name="alias"
                                            labelClassName="text-[10px] font-medium text-cs-muted mb-1"
                                            error={validation.errors.alias}
                                            value={validation.values.alias || ""}
                                            handleChange={validation.handleChange}
                                            
                                            placeholder="e.g. Main card, Operations, Accounting..."
                                        />

                                        {/* Primary radio */}
                                        <div className="flex flex-col gap-1 px-0 md:px-1">
                                            <span className="text-[10px] font-medium text-cs-muted">
                                                Primary payment method
                                            </span>
                                            <div className="flex items-center gap-4 mt-1 text-[9px]">
                                                <label className="flex items-center gap-1.5">
                                                    <input
                                                        type="radio"
                                                        name="principal"
                                                        value="true"
                                                        checked={
                                                            validation.values.principal ===
                                                            true
                                                        }
                                                        onChange={() =>
                                                            validation.setFieldValue(
                                                                "principal",
                                                                true
                                                            )
                                                        }
                                                        className="w-3 h-3"
                                                    />
                                                    <span className="text-cs-dark">
                                                        Yes, set as primary
                                                    </span>
                                                </label>
                                                <label className="flex items-center gap-1.5">
                                                    <input
                                                        type="radio"
                                                        name="principal"
                                                        value="false"
                                                        checked={
                                                            validation.values.principal ===
                                                            false
                                                        }
                                                        onChange={() =>
                                                            validation.setFieldValue(
                                                                "principal",
                                                                false
                                                            )
                                                        }
                                                        className="w-3 h-3"
                                                    />
                                                    <span className="text-cs-muted">
                                                        No, keep as secondary
                                                    </span>
                                                </label>
                                            </div>
                                            {validation.errors.principal && (
                                                <p className="text-cs-danger text-[8px] mt-1">
                                                    {validation.errors.principal}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="px-5 py-4 bg-cs-bg border-t border-cs-border flex justify-end">
                                        <CustomButton
                                            loading={loading}
                                            textLoad="Saving..."
                                            type="success"
                                        >
                                            {validation.values.id
                                                ? "Save changes"
                                                : "Add method"}
                                        </CustomButton>
                                    </div>
                                </div>
                            </form>
                        </>
                        :
                        <>
                            {paymentMethods?.length > 0 ? (
                                <div className="border rounded-xl p-4 bg-cs-bg flex items-center justify-between gap-4">
                                    {/* Left */}
                                    <div className="flex items-center gap-3">
                                        {(changeMethod || !selectedMethod.id) ?
                                            <>
                                                <CustomSelect
                                                    label={'Payment Method'}
                                                    name="selectedMethod_id"
                                                    options={paymentMethods}
                                                    value={selectedMethod}        // Valor controlado por Formik
                                                    handleChange={(e) => onChangeMethod(e.target.value)}
                                                />
                                            </>
                                            :
                                            <>
                                                {/* Brand bubble */}
                                                <div className="
                                                w-10 h-10 rounded-full
                                                bg-cs-primary-soft
                                                text-cs-primary
                                                flex items-center justify-center
                                                text-sm font-bold
                                            "
                                                >
                                                    💳
                                                </div>
                                                {/* Card info */}
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-semibold text-cs-dark">
                                                        {selectedMethod.alias || "Payment card"}
                                                    </span>
                                                    <span className="text-xs text-cs-muted">
                                                        •••• {selectedMethod.card}
                                                    </span>
                                                    <span className="text-[10px] text-cs-muted">
                                                        {selectedMethod.name}
                                                    </span>
                                                </div>
                                            </>
                                        }
                                    </div>


                                    {/* Change */}
                                    <button
                                        type="button"
                                        onClick={() => setChangeMethod(true)}
                                        className="
                                            text-xs text-cs-primary
                                            font-medium
                                            hover:underline
                                            "
                                    >
                                        Change
                                    </button>
                                </div>
                            ) : (
                                <p className="text-sm text-cs-muted mb-2">
                                    No payment methods added yet
                                </p>
                            )}

                            <button
                                type="button"
                                onClick={() => setAddMethod(true)}
                                className="text-sm text-cs-primary font-medium hover:underline"
                            >
                                + Add payment method
                            </button>
                        </>
                    }
                </div>

                {/* Price breakdown */}
                <div className="border-t text-sm mb-1">
                    <div className="flex justify-between ">
                        <span className="text-cs-muted">Subtotal</span>
                        <span>${price.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between ">
                        <span className="text-cs-muted">Taxes ({taxes}%)</span>
                        <span>${taxAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base border-t pt-1">
                        <span>Total</span>
                        <span className="text-cs-primary">
                            ${total.toFixed(2)}
                        </span>
                    </div>
                </div>

                {/* CTA */}
                <CustomButton
                    loading={loading}
                    onClick={save}
                    className="w-full"
                    isDisabled={requiresAdjustment || !selectedMethod?.id}
                >
                    {requiresAdjustment ? "Adjust usage first" : "Confirm & Pay"}
                </CustomButton>

                <p className="text-[11px] text-cs-muted text-center mt-1">
                    🔒 Secure payment Stripe · Cancel anytime
                </p>
            </div>
        </BaseModalAnimated>
    );
}